import config from "appConfig";
import { getLinkForCourseLesson } from "../../../../services/Utils";

import lesson0403 from "../lesson-03/lesson";
import lesson0406 from "../lesson-06/lesson";
const linkLesson0403 = getLinkForCourseLesson("e0db4477-1995-4380-9c35-2338dcf5e504", lesson0403.id);
const linkLesson0406 = getLinkForCourseLesson("e0db4477-1995-4380-9c35-2338dcf5e504", lesson0406.id);

const theory = `<h3 class=sectionHead>Introduction</h3>
<p>In this Lesson, a moving coil loudspeaker is modelled [1, 2, 3] in order to calculate its electrical impedance and sensitivity response curves as a function of the frequency, knowing the loudspeaker fundamental parameters.
<h3 class=sectionHead>Principle</h3>
<p>
   A schematic diagram of a moving coil loudspeaker is shown in <a href=# data-img-alt="Figure 1. Moving coil loudspeaker parts." data-img-src=${config.ASSETS_URL}/04/18/theory/figure-1.svg>Figure 1</a>. A voice coil and its support are rigidly attached to a diaphragm, constituting the moving mass. By construction, the coil is surrounded by a constant radial magnetic field created by a magnet and guided by the motor structure. The moving mass is attached to the basket by both the surround suspension and the spider.<br class=newline>
   <figure class=figure>
<p><img alt="Figure 1. Moving coil loudspeaker parts." src=${config.ASSETS_URL}/04/18/theory/figure-1.svg><figcaption class=caption>Figure 1. Moving coil loudspeaker parts.</figcaption></figure>
<p>
   A picture of a coil and its former, a spider and the diaphragm dome is given in <a href=# data-img-alt="Figure 2. Coil on its former, spider and dust cap of a moving coil driver." data-img-src=${config.ASSETS_URL}/04/18/theory/figure-2.jpg>Figure 2</a>.
   <figure class=figure>
<p><img alt="Figure 2. Coil on its former, spider and dust cap of a moving coil driver." src=${config.ASSETS_URL}/04/18/theory/figure-2.jpg><figcaption class=caption>Figure 2. Coil on its former, spider and dust cap of a moving coil driver.</figcaption></figure>
<p>
   The principle of operation is reciprocal to that studied for the electrodynamic microphone. A variable electrical voltage
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>U</mi>
   </math>
   is applied to the loudspeaker coil and a current
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>I</mi>
   </math>
   then flows through it. An axial Lorentz force of amplitude
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>L</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=italic>BℓI</mi>
   </math>
   is then created which sets the moving mass in vibration with a velocity
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
   </math>. The membrane therefore radiates an acoustic wave. At the same time, the movement of the coil in the air gap creates an induced EMF of amplitude
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>e</mi>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=italic>Bℓ</mi>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
   </math>
   which tries to oppose any change caused by the voice-coil current.
<h3 class=sectionHead>Behavioural equations</h3>
<p>In order to build a simplified model of the moving coil loudspeaker, its behaviour is considered to be linear: the displacement of the diaphragm remains small, no eddy currents, no hysteretic phenomena. We also work in the low frequency domain, which leads to the following assumptions:
<ul class=itemize1>
   <li class=itemize>
      the diaphragm can be assimilated to a rigid flat piston of surface
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>S</mi>
            </mrow>
            <mrow>
               <mi>d</mi>
            </mrow>
         </msub>
      </math>,
   <li class=itemize>
      the moving parts are assimilated to a 1-DOF damped mechanical oscillator of impedance
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>Z</mi>
            </mrow>
            <mrow>
               <mi>m</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi>m</mi>
            </mrow>
         </msub>
         <mo class=MathClass-bin>+</mo>
         <mi mathvariant=italic>jω</mi>
         <msub>
            <mrow>
               <mi>M</mi>
            </mrow>
            <mrow>
               <mi>m</mi>
            </mrow>
         </msub>
         <mo class=MathClass-bin>+</mo>
         <mfrac>
            <mrow>
               <mn>1</mn>
            </mrow>
            <mrow>
               <mi mathvariant=italic>jω</mi>
               <msub>
                  <mrow>
                     <mi>C</mi>
                  </mrow>
                  <mrow>
                     <mi>m</mi>
                  </mrow>
               </msub>
            </mrow>
         </mfrac>
      </math>. The compliance
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>C</mi>
            </mrow>
            <mrow>
               <mi>m</mi>
            </mrow>
         </msub>
      </math>
      corresponds to the equivalent compliance of the spider and surround suspension,
   <li class=itemize>the loudspeaker is mounted on an infinite a baffle to get a known analytical expression of the radiation impedance. This impedance also has a simplified form valid at low frequencies,
   <li class=itemize>the presence of the magnet and the basket are neglected in the calculation of the rear radiation.
</ul>
<p>
   We can now write the behavioural equations starting with the electrical equation. The driver is connected to an amplifier which delivers the voltage
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>U</mi>
   </math>. By writing Kirchhoff’s voltage law, we get
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>U</mi>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>e</mi>
               </mrow>
            </msub>
            <mi>I</mi>
            <mo class=MathClass-bin>+</mo>
            <mi mathvariant=italic>Bℓ</mi>
            <msub>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>+</mo>
      <mi mathvariant=italic>jω</mi>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
   </math>
   is the coil electrical impedance and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>Bℓ</mi>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
   </math>
   is the EMF caused by the coil vibration.<br class=newline>For the mechanical domain, the second law of Newton yields
<table class=equation>
   <tr>
      <td style="font-size: 11px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mi mathvariant=italic>BℓI</mi>
            <mo class=MathClass-bin>−</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>V</mi>
                     </mrow>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>jω</mi>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>F</mi>
               </mrow>
               <mrow>
                  <mi>a</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mi mathvariant=italic>jω</mi>
            <msub>
               <mrow>
                  <mi>M</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
         </mrow>
      </msub>
   </math>
   is force caused by the air on both side of the membrane. Note that in the left-hand side of the above equation, the Lorentz force that causes the membrane to vibrate has a positive sign, while all other terms that oppose the motion have a negative sign.<br class=newline>The force
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
         </mrow>
      </msub>
   </math>
   exerted by the air on the membrane is created by the difference in acoustic pressure between the two sides
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>F</mi>
               </mrow>
               <mrow>
                  <mi>a</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>f</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(3)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi>f</mi>
         </mrow>
      </msub>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi>r</mi>
         </mrow>
      </msub>
   </math>
   respectively are the acoustic pressures on the front and rear sides of the diaphragm. They can be calculated using the low frequency approximation of the baffled piston radiation impedance (see <a href="${linkLesson0406}" target="_blank">Lesson 6</a>)
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>f</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ar</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>Q</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
          </math>
      <td class=eq-no>(4)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
   </math>
   is the diaphragm volume velocity and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ar</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ar</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>+</mo>
      <mi mathvariant=italic>jω</mi>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ar</mi>
         </mrow>
      </msub>
   </math>
   is the low frequency approximation of the radiation impedance. We then get
<table class=equation>
   <tr>
      <td style="font-size: 14px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>F</mi>
               </mrow>
               <mrow>
                  <mi>a</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>f</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mn>2</mn>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ar</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>Q</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
         </math>
      <td class=eq-no>(5)
</table>
<p>Finally, the three (one for each domain) behavioural equations can be put into the form of the following coupled system
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mrow>
               <mo form=prefix fence=true>{</mo>
               <mrow>
                  <mtable class=array align=axis style="" equalcolumns=false equalrows=false>
                     <mtr>
                        <mtd columnalign=center class=array>
                           <mi>U</mi>
                           <mo class=MathClass-rel>=</mo>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mi>e</mi>
                              </mrow>
                           </msub>
                           <mi>I</mi>
                           <mo class=MathClass-bin>+</mo>
                           <mi mathvariant=italic>Bℓ</mi>
                           <msub>
                              <mrow>
                                 <mi>V</mi>
                              </mrow>
                              <mrow>
                                 <mi>d</mi>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd columnalign=center class=array>
                           <mi mathvariant=italic>BℓI</mi>
                           <mo class=MathClass-rel>=</mo>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mi>m</mi>
                              </mrow>
                           </msub>
                           <msub>
                              <mrow>
                                 <mi>V</mi>
                              </mrow>
                              <mrow>
                                 <mi>d</mi>
                              </mrow>
                           </msub>
                           <mo class=MathClass-bin>+</mo>
                           <msub>
                              <mrow>
                                 <mi>F</mi>
                              </mrow>
                              <mrow>
                                 <mi>a</mi>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd columnalign=center class=array>
                           <msub>
                              <mrow>
                                 <mi>F</mi>
                              </mrow>
                              <mrow>
                                 <mi>a</mi>
                              </mrow>
                           </msub>
                           <mo class=MathClass-rel>=</mo>
                           <mn>2</mn>
                           <msub>
                              <mrow>
                                 <mi>S</mi>
                              </mrow>
                              <mrow>
                                 <mi>d</mi>
                              </mrow>
                           </msub>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mi mathvariant=italic>ar</mi>
                              </mrow>
                           </msub>
                           <msub>
                              <mrow>
                                 <mi>Q</mi>
                              </mrow>
                              <mrow>
                                 <mi>d</mi>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
            </mrow>
         </math>
      <td class=eq-no>(6)
</table>
<h3 class=sectionHead>Electrical impedance</h3>
<p>
   Using the coupled system of equations (6), it is possible to derive the electrical impedance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>et</mi>
         </mrow>
      </msub>
   </math>
   of the transducer, <em><span class=cmti-10>i.e.</span></em> the electrical impedance of the coil attached to the diaphragm and immersed in the magnetic field
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>B</mi>
   </math>. First, taking the third equation of system (6) and replacing it in the second one yields
<table class=equation>
   <tr>
      <td style="font-size: 8px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mi mathvariant=italic>BℓI</mi>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mn>2</mn>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ar</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>Q</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mn>2</mn>
            <msubsup>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msubsup>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ar</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ms</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
         </math>
      <td class=eq-no>(7)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
   </math>
   is the mechanical impedance of the moving mass including the radiation terms. It corresponds to a 1-DOF system with
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mrow>
               <mo form=prefix fence=true>{</mo>
               <mrow>
                  <mtable class=array align=axis style="" equalcolumns=false equalrows=false>
                     <mtr>
                        <mtd columnalign=center class=array>
                           <msub>
                              <mrow>
                                 <mi>R</mi>
                              </mrow>
                              <mrow>
                                 <mi mathvariant=italic>ms</mi>
                              </mrow>
                           </msub>
                           <mo class=MathClass-rel>=</mo>
                           <msub>
                              <mrow>
                                 <mi>R</mi>
                              </mrow>
                              <mrow>
                                 <mi>m</mi>
                              </mrow>
                           </msub>
                           <mo class=MathClass-bin>+</mo>
                           <mn>2</mn>
                           <msubsup>
                              <mrow>
                                 <mi>S</mi>
                              </mrow>
                              <mrow>
                                 <mi>d</mi>
                              </mrow>
                              <mrow>
                                 <mn>2</mn>
                              </mrow>
                           </msubsup>
                           <msub>
                              <mrow>
                                 <mi>R</mi>
                              </mrow>
                              <mrow>
                                 <mi mathvariant=italic>ar</mi>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd columnalign=center class=array>
                           <msub>
                              <mrow>
                                 <mi>M</mi>
                              </mrow>
                              <mrow>
                                 <mi mathvariant=italic>ms</mi>
                              </mrow>
                           </msub>
                           <mo class=MathClass-rel>=</mo>
                           <msub>
                              <mrow>
                                 <mi>M</mi>
                              </mrow>
                              <mrow>
                                 <mi>m</mi>
                              </mrow>
                           </msub>
                           <mo class=MathClass-bin>+</mo>
                           <mn>2</mn>
                           <msubsup>
                              <mrow>
                                 <mi>S</mi>
                              </mrow>
                              <mrow>
                                 <mi>d</mi>
                              </mrow>
                              <mrow>
                                 <mn>2</mn>
                              </mrow>
                           </msubsup>
                           <msub>
                              <mrow>
                                 <mi>M</mi>
                              </mrow>
                              <mrow>
                                 <mi mathvariant=italic>ar</mi>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd columnalign=center class=array>
                           <msub>
                              <mrow>
                                 <mi>C</mi>
                              </mrow>
                              <mrow>
                                 <mi mathvariant=italic>ms</mi>
                              </mrow>
                           </msub>
                           <mo class=MathClass-rel>=</mo>
                           <msub>
                              <mrow>
                                 <mi>C</mi>
                              </mrow>
                              <mrow>
                                 <mi>m</mi>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
            </mrow>
         </math>
      <td class=eq-no>(8)
</table>
<p>
   Note that the mass and loss terms of the air load are added to the value of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
   </math>
   respectively, the compliance term remains unchanged.<br class=newline>By taking Eq. (7) and replacing it in the first equation of system (6), we get
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>U</mi>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
      <mi>I</mi>
      <mo class=MathClass-bin>+</mo>
      <mi>B</mi>
      <msup>
         <mrow>
            <mi>ℓ</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
      <mi>I</mi>
      <mo class=MathClass-bin>∕</mo>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
   </math>, and finally the expression of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>et</mi>
         </mrow>
      </msub>
   </math>
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>et</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>U</mi>
               </mrow>
               <mrow>
                  <mi>I</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>e</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <mi>B</mi>
                  <msup>
                     <mrow>
                        <mi>ℓ</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ms</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(9)
</table>
<p>
   Please note that
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>B</mi>
      <msup>
         <mrow>
            <mi>ℓ</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
   </math>
   stands for
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msup>
         <mrow>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=italic>Bℓ</mi>
            <mo class=MathClass-close>)</mo>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
   </math>.
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>et</mi>
         </mrow>
      </msub>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
   </math>
   are plotted in <a href=# data-img-alt="Figure 3. Speaker impedance as a function of frequency." data-img-src=${config.ASSETS_URL}/04/18/theory/figure-3.svg>Figure 3</a> for a generic
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>10</mn>
   </math>
   cm diameter driver. An interesting difference between the two curves is the bump in the low frequency range which is due to the
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>B</mi>
      <msup>
         <mrow>
            <mi>ℓ</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
      <mo class=MathClass-bin>∕</mo>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
   </math>
   term. The maximum of the bump is reached when
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
   </math>
   is minimum, which occurs at the resonance frequency
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>
   of the moving parts (which also include the radiation masses).
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>
   is given by (see <a href="${linkLesson0403}" target="_blank">Lesson 3</a>)
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>f</mi>
               </mrow>
               <mrow>
                  <mi>s</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi>π</mi>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <msqrt>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>C</mi>
                           </mrow>
                           <mrow>
                              <mi mathvariant=italic>ms</mi>
                           </mrow>
                        </msub>
                        <msub>
                           <mrow>
                              <mi>M</mi>
                           </mrow>
                           <mrow>
                              <mi mathvariant=italic>ms</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </msqrt>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(10)
</table>
<figure class=figure>
   <p>
      <img alt="Figure 3. Speaker impedance as a function of frequency." src=${config.ASSETS_URL}/04/18/theory/figure-3.svg>
      <figcaption class=caption>Figure 3. Speaker impedance as a function of frequency.</figcaption>
</figure>
<p>
   It is interesting to see that an electrical quantity, the impedance of the loudspeaker, also depends, thanks to electrodynamic coupling, on mechanical and acoustic quantities. This property is widely used to measure loudspeaker parameters because electrical quantities are often easier to measure than mechanical or acoustic quantities: a good quality sound card is sufficient to measure
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>U</mi>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>I</mi>
   </math>
   accurately.
<h3 class=sectionHead>Diaphragm vibration</h3>
<p>
   The coupling equation also allows the calculation of the diaphragm velocity of the speaker, which is required to compute the radiated pressure. By replacing
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>I</mi>
   </math>
   in the first equation of the system (6) with its expression taken from Eq. (7), we get
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>U</mi>
            <mo class=MathClass-rel>=</mo>
            <mrow>
               <mo form=prefix fence=true>(</mo>
               <mrow>
                  <mfrac>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>Z</mi>
                           </mrow>
                           <mrow>
                              <mi>e</mi>
                           </mrow>
                        </msub>
                        <msub>
                           <mrow>
                              <mi>Z</mi>
                           </mrow>
                           <mrow>
                              <mi mathvariant=italic>ms</mi>
                           </mrow>
                        </msub>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>Bℓ</mi>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-bin>+</mo>
                  <mi mathvariant=italic>Bℓ</mi>
               </mrow>
               <mo form=postfix fence=true>)</mo>
            </mrow>
            <msub>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
         </math>
      <td class=eq-no>(11)
</table>
<p>yielding
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>BℓU</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ms</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <mi>B</mi>
                  <msup>
                     <mrow>
                        <mi>ℓ</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(12)
</table>
<p>
   As
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
      <mi>ω</mi>
      <mo class=MathClass-rel>≪</mo>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
   </math>
   at low frequencies,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
   </math>
   reaches its maximum value at
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>
   for which
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
   </math>,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msubsup>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>max</mi>
         </mrow>
      </msubsup>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
      <mo class=MathClass-close>)</mo>
   </math>
   is then given by
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msubsup>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>max</mi>
               </mrow>
            </msubsup>
            <mo class=MathClass-rel>≃</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>BℓU</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ms</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <mi>B</mi>
                  <msup>
                     <mrow>
                        <mi>ℓ</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(13)
</table>
<p>
   The relative diaphragm velocity in dB <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo>(</mo><mn>20</mn>
      <mi class=qopname>log</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>∕</mo>
      <msubsup>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>max</mi>
         </mrow>
      </msubsup>
      <mo class=MathClass-close>)</mo>
    <mo class=MathClass-close>)</mo>
   </math> is plotted in <a href=# data-img-alt="Figure 4. Diaphragm velocity <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>V</mi></mrow><mrow><mi>d</mi></mrow></msub></math> as a function of frequency." data-img-src=${config.ASSETS_URL}/04/18/theory/figure-4.svg>Figure 4</a> as well as the relative diaphragm velocity low frequency approximation computed with
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
   </math>. The approximated expression of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
   </math>
   corresponds to a second order bandpass filter, a third order is added at high frequencies when taking into account the
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
      <mi>ω</mi>
   </math>
   term.
   <figure class=figure>
<p><img alt="Figure 4. Diaphragm velocity <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>V</mi></mrow><mrow><mi>d</mi></mrow></msub></math> as a function of frequency." src=${config.ASSETS_URL}/04/18/theory/figure-4.svg><figcaption class=caption>Figure 4. Diaphragm velocity <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>V</mi></mrow><mrow><mi>d</mi></mrow></msub></math> as a function of frequency.</figcaption></figure>
<p>
   It is also possible to calculate the displacement
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>X</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>∕</mo>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant=italic>jω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   and the acceleration
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>A</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=italic>jω</mi>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
   </math>
   of the diaphragm as a function of frequency. Thus, the relative displacement in dB
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-open>(</mo>
      <mn>20</mn>
      <mi class=qopname>log</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <msub>
         <mrow>
            <mi>X</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>∕</mo>
      <msubsup>
         <mrow>
            <mi>X</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>max</mi>
         </mrow>
      </msubsup>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-close>)</mo>
   </math>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msubsup>
         <mrow>
            <mi>X</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>max</mi>
         </mrow>
      </msubsup>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=italic>BℓU</mi>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>∕</mo>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
   </math>
   is shown in <a href=# data-img-alt="Figure 5. Diaphragm displacement <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>X</mi></mrow><mrow><mi>d</mi></mrow></msub></math> as a function of frequency." data-img-src=${config.ASSETS_URL}/04/18/theory/figure-5.svg>Figure 5</a>. It can be seen that the displacement response has the shape of a second order low-pass filter when
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>≃</mo>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
   </math>.
   <figure class=figure>
<p><img alt="Figure 5. Diaphragm displacement <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>X</mi></mrow><mrow><mi>d</mi></mrow></msub></math> as a function of frequency." src=${config.ASSETS_URL}/04/18/theory/figure-5.svg><figcaption class=caption>Figure 5. Diaphragm displacement <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>X</mi></mrow><mrow><mi>d</mi></mrow></msub></math> as a function of frequency.</figcaption></figure>
<p>
   The relative acceleration in dB
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-open>(</mo>
      <mn>20</mn>
      <mi class=qopname>log</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <msub>
         <mrow>
            <mi>A</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>∕</mo>
      <msubsup>
         <mrow>
            <mi>A</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>max</mi>
         </mrow>
      </msubsup>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-close>)</mo>
   </math>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msubsup>
         <mrow>
            <mi>A</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>max</mi>
         </mrow>
      </msubsup>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=italic>BℓU</mi>
      <mo class=MathClass-bin>∕</mo>
      <mo class=MathClass-open>(</mo>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
      <mo class=MathClass-close>)</mo>
   </math>
   is shown in <a href=# data-img-alt="Figure 6. Diaphragm acceleration <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>A</mi></mrow><mrow><mi>d</mi></mrow></msub></math> as a function of frequency." data-img-src=${config.ASSETS_URL}/04/18/theory/figure-6.svg>Figure 6</a>. It can be seen that the acceleration response has the shape of a second order high-pass filter for
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>≃</mo>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
   </math>.
   <figure class=figure>
<p><img alt="Figure 6. Diaphragm acceleration <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>A</mi></mrow><mrow><mi>d</mi></mrow></msub></math> as a function of frequency." src=${config.ASSETS_URL}/04/18/theory/figure-6.svg><figcaption class=caption>Figure 6. Diaphragm acceleration <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>A</mi></mrow><mrow><mi>d</mi></mrow></msub></math> as a function of frequency.</figcaption></figure>
<h3 class=sectionHead>On-axis pressure response</h3>
<p>
   Once the diaphragm velocity is known, it can be used to compute the on axis pressure level at distance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>r</mi>
   </math>
   using the baffle piston model (see <a href="${linkLesson0406}" target="_blank">Lesson 6</a>)
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ax</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>r</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mi mathvariant=italic>jω</mi>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jkr</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi mathvariant=italic>πr</mi>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(14)
</table>
<p>
   This quantity is generally evaluated at
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>r</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
   </math>
   m for a input electrical power of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>P</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
   </math>
   W. For an
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>8</mn>
   </math>
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>Ω</mi>
   </math>
   nominal impedance, the input voltage
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>U</mi>
   </math>
   is equal to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>U</mi>
      <mo class=MathClass-rel>=</mo>
      <msqrt>
         <mrow>
            <mn>8</mn>
            <mo class=MathClass-bin>×</mo>
            <msub>
               <mrow>
                  <mi>P</mi>
               </mrow>
               <mrow>
                  <mi>e</mi>
               </mrow>
            </msub>
         </mrow>
      </msqrt>
      <mo class=MathClass-rel>=</mo>
      <mn>2</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>83</mn>
   </math>
   V.<br class=newline>The on-axis pressure level at
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>1</mn>
   </math>
   m for
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>1</mn>
   </math>
   W of a generic
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>10</mn>
   </math>
   cm driver is plotted in dB SPL in <a href=# data-img-alt="Figure 7. On-axis pressure level for <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mn>1</mn></math> W (<math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mn>2</mn><mo class=MathClass-punc>.</mo><mn>83</mn></math> V) as a function of frequency." data-img-src=${config.ASSETS_URL}/04/18/theory/figure-7.svg>Figure 7</a>. It has the shape of a 2nd order high-pass filter when the inductance is ignored 
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mo>(</mo>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
       <mo>)</mo>
   </math> and a bandpass filter when the inductance value is taken into account. This result may seem surprising, one would have expected the loudspeaker to operate around its resonant frequency like the moving coil microphone, but this is not the case. Indeed, due to the process of radiation in air which provides an
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>ω</mi>
   </math>
   term in Eq. (14) (it is easier to radiate high frequencies), the on-axis pressure is then proportional to the acceleration of the diaphragm
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>A</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi>ω</mi>
      <mo class=MathClass-bin>×</mo>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
   </math>, making a voice coil loudspeaker more effective above
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>. The plateau value at
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>1</mn>
   </math>
   m is given by
<table class=equation>
   <tr>
      <td style="font-size: 8px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mn>20</mn>
            <mi class=qopname>log</mi>
            <mo>⁡</mo>
            <mfrac>
               <mrow>
                  <mi>ω</mi>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>S</mi>
                     </mrow>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>V</mi>
                     </mrow>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi>π</mi>
                  <mo class=MathClass-bin>×</mo>
                  <mn>2</mn>
                  <mo class=MathClass-punc>.</mo>
                  <mn>1</mn>
                  <msup>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mn>5</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>≃</mo>
            <mn>20</mn>
            <mi class=qopname>log</mi>
            <mo>⁡</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>S</mi>
                     </mrow>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                  </msub>
                  <mi mathvariant=italic>BℓU</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi>π</mi>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>M</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ms</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>×</mo>
                  <mn>2</mn>
                  <mo class=MathClass-punc>.</mo>
                  <mn>1</mn>
                  <msup>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mn>5</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(15)
</table>
<p>
   According to Eq. (15), a high sensitivity requires large diaphragm surface,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>Bℓ</mi>
   </math>
   factor as well as low voice coil resistance and moving mass. Be aware that some parameters are not independent, increasing
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
   </math>
   also increases
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
   </math>, increasing
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>ℓ</mi>
   </math>
   increases both
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
   </math>.<br class=newline>Since a single driver cannot provide both high efficiency and full bandwidth, two to four drivers are usually used to make HiFi speakers. These speakers are named according to their bandwidth (see <a href=# data-img-alt="Table 1. Driver names according to their operating bandwidth." data-img-src=${config.ASSETS_URL}/04/18/theory/table-1.svg>Table 1</a>).
   <figure class=figure>
<p><img alt="Figure 7. On-axis pressure level for <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mn>1</mn></math> W (<math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mn>2</mn><mo class=MathClass-punc>.</mo><mn>83</mn></math> V) as a function of frequency." src=${config.ASSETS_URL}/04/18/theory/figure-7.svg><figcaption class=caption>Figure 7. On-axis pressure level for <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mn>1</mn></math> W (<math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mn>2</mn><mo class=MathClass-punc>.</mo><mn>83</mn></math> V) as a function of frequency.</figcaption></figure>
<div class=table>
   <figure class=float>
      <figcaption class=caption>Table 1. Driver names according to their operating bandwidth.</figcaption>
      <img alt="Table 1. Driver names according to their operating bandwidth." src=${config.ASSETS_URL}/04/18/theory/table-1.svg>
   </figure>
</div>
<h3 class=sectionHead>Conclusion</h3>
<p>
   In this Lesson, a simple low-frequency model of a moving-coil loudspeaker was developed and used to calculate the electrical impedance, diaphragm velocity and on-axis pressure level as a function of frequency. However, it is questionable how accurate this model is. The theoretical results should be relevant as long as the assumptions made are verified (small displacements, wavelength large compared to the dimensions of the speaker, uniformly vibrating diaphragm, etc.). As an example, the measured on-axis pressure level of a
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>10</mn>
   </math>
    cm driver is given in <a href=# data-img-alt="Figure 8. On-axis pressure level for 1 W of a Beyma 4FR40 driver as a function of frequency." data-img-src=${config.ASSETS_URL}/04/18/theory/figure-8.svg>Figure 8</a>. The typical shape of a high pass filter is found up to a few kHz. Above this frequency, membrane modes and eddy currents cause the model to decrease its accuracy. Some of the speaker model limitations will be studied in more detail in future Lessons.
   <figure class=figure>
<p><img alt="Figure 8. On-axis pressure level for 1 W of a Beyma 4FR40 driver as a function of frequency." src=${config.ASSETS_URL}/04/18/theory/figure-8.svg><figcaption class=caption>Figure 8. On-axis pressure level for 1 W of a Beyma 4FR40 driver as a function of frequency.</figcaption></figure>
<h3 class=likesectionHead>Glossary</h3>
<p>
<dl>
  <dt>1-DOF
  <dd>1 Degree Of Freedom
  <dt>EMF
  <dd>ElectroMotive Force
  <dt>HiFi
  <dd>High Fidelity
</dl>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt id=X0-Beranek2012 class=thebibliography>[1]
   <dd id=bib-1 class=thebibliography>
      <p>Leo Beranek and Tim Mellow. <span class=cmti-10>Acoustics: sound fields and transducers</span>. Academic Pres, 2012. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 9780123914866.
   <dt id=X0-Kleiner2013 class=thebibliography>[2]
   <dd id=bib-2 class=thebibliography>
      <p>Mendel Kleiner. <span class=cmti-10>Electroacoustics</span>. CRC Press, 2013. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 9780429073519.
   <dt id=X0-Rossi1988 class=thebibliography>[3]
   <dd id=bib-3 class=thebibliography>
      <p>Mario Rossi. <span class=cmti-10>Acoustics and Electroacoustics</span>. Artech House, 1988. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 978-0890062555.
</dl>`;

export default theory;
